// Listing-mode switcher for the Address Book (companies) module.
//
// Mirrors the CURRENT prod pattern (`src/inventory/InventoryLayout.tsx` and
// `src/component/ComponentLayout.tsx` on aeronetsoftware/cloud/aeronet-frontend
// @develop, commit 8a67bfc1 "ANX-2475"): a breadcrumb whose last segment is a
// hover/click dropdown built from the shared <BreadcrumbHoverRoot> +
// <BreadcrumbDropdownMenu> + <BreadcrumbMenuLinkItem>, with the modes as ROUTES
// and an <Outlet/> beneath. Same shape our OpportunityLayout already uses.
//
// This replaces a local-only switcher that lived on the SHARED ListingHeader as
// a `modes`/`currentMode`/`onModeChange` prop trio. Prod's Header.tsx has no
// such API — Address Book was its only caller — so the switcher moved here and
// Header.tsx went back to matching prod.
//
// LOCAL ADAPTATION vs prod: prod's layouts key their breadcrumb off ABSOLUTE
// paths ("/inventory/parts") because those modules are mounted at a fixed path.
// This module is not — App.tsx routes it at `menuItem.to + "/*"` straight from
// the backend nav, so it is "AddressBook" on the G2 default nav and may differ
// per tenant. Hardcoding "/companies/..." would break every tenant mounted
// elsewhere, so the base path is derived from the live pathname instead and the
// mode links are built from it. The rendered breadcrumb is identical to prod's.
import { useEffect } from "react";

import { Box, Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
  describeMode,
} from "../components/BreadcrumbDropdownMenu";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import { ListingRouteBaseProvider } from "../components/datagrid/ListingRouteBase";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";
import { migrateModeSegment } from "../components/renamedModeSegments";

/**
 * Mode segment -> dropdown label. Keys are the child route paths. The KPI mode
 * is "dashboard" — "overview" until H94 (Aaron 2026-09-28: no "overviews" at a
 * module level); CompanyList.tsx redirects the old segment.
 */
export const breadcrumbMenu: { [segment: string]: string } = {
  dashboard: "Dashboard",
  list: "List",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  dashboard: "Register health and mix",
  list: "Every company",
};

export const DEFAULT_MODE_SEGMENT = "dashboard";

// Remember the last mode so landing on the bare module root returns the user
// where they left off. PATTERNS.md's mode-switch section requires this ("the
// user expects the choice to stick across visits") and names the key shape
// `<module>.viewMode`; seven modules already conform (opportunities,
// flight_schedule, defect_reporting, projects, groups, action_register,
// aircraft_roster), so this is the convention, not a new idea. Routes remain
// the source of truth for WHICH mode is showing — localStorage only records
// the last one, exactly as src/opportunities does it.
export const MODE_STORAGE_KEY = "companies.viewMode";

/**
 * Is this a real mode segment? Uses hasOwnProperty, NOT `in` — `in` walks the
 * prototype chain, so "constructor" / "toString" / "valueOf" / "__proto__" all
 * test true against an object literal and would pass as valid modes.
 */
const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(breadcrumbMenu, segment);

/** Redirect target for a bare visit to the module root. */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  // A stored "overview" (pre-H94) reads as "dashboard".
  const stored = migrateModeSegment(window.localStorage.getItem(MODE_STORAGE_KEY));
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

interface CompaniesLayoutProps {
  /** Module title from the nav (e.g. "Address Book"); roots the breadcrumb. */
  title?: string;
}

const CompaniesLayout: React.FC<CompaniesLayoutProps> = ({ title }) => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "companiesBreadcrumbMenu",
  });

  // Split the live pathname so the trailing mode segment can be told apart from
  // the (nav-driven) module base. On "/AddressBook/dashboard" that gives
  // basePath "/AddressBook" and currentMode "dashboard".
  const segments = location.pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const currentMode = onMode ? lastSegment : DEFAULT_MODE_SEGMENT;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the mode
  // links "//dashboard", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "dashboard") and navigates off-site. App.tsx always mounts this module
  // under at least one segment, so that path is unreachable today — but it is
  // reachable in tests (testHelpers pins the URL to "/") and would return the
  // moment the nav hands back an empty `to`.
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  const basePath = baseSegments.length ? "/" + baseSegments.join("/") : "";

  const isAddressBook = segments.some(
    (s) => s.toLowerCase() === "addressbook"
  );
  const resolvedTitle = title || (isAddressBook ? "Address Book" : "Companies");

  useEffect(() => {
    if (onMode) window.localStorage.setItem(MODE_STORAGE_KEY, lastSegment);
  }, [onMode, lastSegment]);

  return (
    <Box height="100%">
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          <LinkRouter to={basePath || "/"}>{resolvedTitle} /</LinkRouter>
          <BreadcrumbHoverRoot popupState={popupState}>
            <Button
              endIcon={<BreadcrumbChevronIcon />}
              sx={breadcrumbCurrentTriggerSx}
            >
              <Typography
                variant="h2"
                component="span"
                sx={breadcrumbCurrentLabelSx}
              >
                {breadcrumbMenu[currentMode]}
              </Typography>
            </Button>
            <BreadcrumbDropdownMenu popupState={popupState}>
              {Object.entries(breadcrumbMenu).map(([segment, label]) => (
                <BreadcrumbMenuLinkItem
                  key={segment}
                  to={`${basePath}/${segment}`}
                  label={label}
                  description={describeMode(MODE_DESCRIPTIONS, segment)}
                  selected={currentMode === segment}
                  onClick={popupState.close}
                />
              ))}
            </BreadcrumbDropdownMenu>
          </BreadcrumbHoverRoot>
        </Title>
      </BreadcrumbHeaderListing>
      {/* The mode routes below add a URL segment the shared list toolbar cannot
          see. Declaring the base here — the same derived `basePath` the mode
          links already use — is what stops its Create FAB resolving to
          <base>/<mode>/create, which matches no route and renders blank.
          Covers every mode under this layout, including ones added later. */}
      <ListingRouteBaseProvider value={{ base: basePath }}>
        <Outlet />
      </ListingRouteBaseProvider>
    </Box>
  );
};

export default CompaniesLayout;
